import React, { useState, useEffect } from "react";
import "./checkout.css";
import Nav from "../../components/nav/nav";
import Footer from "../../components/footer/Footer";
import { FaTruck, FaFileInvoice, FaCreditCard, FaCheckCircle, FaHome, FaRedoAlt, FaCopy, FaShare, FaDownload } from "react-icons/fa";
import { useLocation } from "react-router-dom";

// const API_BASE = "http://localhost:5000/api";
const API_BASE = "https://host.aizoplug.com:3000/ecommerce";
const getToken = () => localStorage.getItem("idToken");

async function apiFetch(path, opts = {}) {
  const res = await fetch(`${API_BASE}${path}`, {
    ...opts,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${getToken()}`,
      ...(opts.headers || {})
    }
  });
  return res.json();
}

const CheckoutPage = () => {
  const location = useLocation();
  const [currentStep, setCurrentStep] = useState(() => {
    // Get saved step from sessionStorage
    const savedStep = sessionStorage.getItem("checkout_step");
    return savedStep ? parseInt(savedStep) : 1;
  });
  const [paymentMethod, setPaymentMethod] = useState("razorpay");
  const [saveInfo, setSaveInfo] = useState(false);
  const [walletBalance, setWalletBalance] = useState(0);
  const [loading, setLoading] = useState(false);
  const [placedOrderId, setPlacedOrderId] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState(null); // success | failed
  const [paymentMessage, setPaymentMessage] = useState("");
  const [orderDetails, setOrderDetails] = useState(null); // Store order details for success page

  // Form state
  const [formData, setFormData] = useState(() => {
    // Try to restore form data from sessionStorage
    const savedFormData = sessionStorage.getItem("checkout_form_data");
    if (savedFormData) {
      try {
        return JSON.parse(savedFormData);
      } catch (e) {
        return {
          email: "",
          phone: "",
          firstName: "",
          lastName: "",
          address: "",
          apartment: "",
          city: "",
          state: "",
          pincode: "",
          country: "India",
          sameAsShipping: false,
          billingFirstName: "",
          billingLastName: "",
          billingAddress: "",
          billingApartment: "",
          billingCity: "",
          billingState: "",
          billingPincode: "",
          billingPostOffice: "",
          billingCountry: "India",
          cardNumber: "",
          cardName: "",
          expiry: "",
          cvv: "",
          upiId: "",
          postOffice: "",
          saveCard: false
        };
      }
    }
    return {
      email: "",
      phone: "",
      firstName: "",
      lastName: "",
      address: "",
      apartment: "",
      city: "",
      state: "",
      pincode: "",
      country: "India",
      sameAsShipping: false,
      billingFirstName: "",
      billingLastName: "",
      billingAddress: "",
      billingApartment: "",
      billingCity: "",
      billingState: "",
      billingPincode: "",
      billingPostOffice: "",
      billingCountry: "India",
      cardNumber: "",
      cardName: "",
      expiry: "",
      cvv: "",
      upiId: "",
      postOffice: "",
      saveCard: false
    };
  });

  const [postOffices, setPostOffices] = useState([]);
  const [loadingPin, setLoadingPin] = useState(false);
  const [pinError, setPinError] = useState("");
  const [billingPostOffices, setBillingPostOffices] = useState([]);
  const [billingLoadingPin, setBillingLoadingPin] = useState(false);
  const [billingPinError, setBillingPinError] = useState("");

  // Saved addresses — fetched from API
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [selectedAddress, setSelectedAddress] = useState(() => {
    const savedSelected = sessionStorage.getItem("checkout_selected_address");
    return savedSelected ? parseInt(savedSelected) : null;
  });
  const [showNewAddressForm, setShowNewAddressForm] = useState(() => {
    const savedShowNew = sessionStorage.getItem("checkout_show_new_address");
    return savedShowNew ? savedShowNew === "true" : true;
  });

  // Saved billing address id (set after saving)
  const [shippingAddressId, setShippingAddressId] = useState(() => {
    const savedId = sessionStorage.getItem("checkout_shipping_address_id");
    return savedId ? parseInt(savedId) : null;
  });
  const [billingAddressId, setBillingAddressId] = useState(() => {
    const savedId = sessionStorage.getItem("checkout_billing_address_id");
    return savedId ? parseInt(savedId) : null;
  });
  useEffect(() => {
    // If user comes from cart → reset checkout
    if (location.state?.fromCart) {
      sessionStorage.removeItem("checkout_step");
      setCurrentStep(1);
      setPaymentStatus(null);
    }
  }, [location.state]);
  // Save state to sessionStorage whenever it changes
  useEffect(() => {
    sessionStorage.setItem("checkout_step", currentStep);
  }, [currentStep]);

  useEffect(() => {
    sessionStorage.setItem("checkout_form_data", JSON.stringify(formData));
  }, [formData]);

  useEffect(() => {
    if (selectedAddress) {
      sessionStorage.setItem("checkout_selected_address", selectedAddress);
    } else {
      sessionStorage.removeItem("checkout_selected_address");
    }
  }, [selectedAddress]);

  useEffect(() => {
    sessionStorage.setItem("checkout_show_new_address", showNewAddressForm);
  }, [showNewAddressForm]);

  useEffect(() => {
    if (shippingAddressId) {
      sessionStorage.setItem("checkout_shipping_address_id", shippingAddressId);
    } else {
      sessionStorage.removeItem("checkout_shipping_address_id");
    }
  }, [shippingAddressId]);

  useEffect(() => {
    if (billingAddressId) {
      sessionStorage.setItem("checkout_billing_address_id", billingAddressId);
    } else {
      sessionStorage.removeItem("checkout_billing_address_id");
    }
  }, [billingAddressId]);

  // Clear checkout data on successful order completion
  const clearCheckoutData = () => {
    sessionStorage.removeItem("checkout_step");
    sessionStorage.removeItem("checkout_form_data");
    sessionStorage.removeItem("checkout_selected_address");
    sessionStorage.removeItem("checkout_show_new_address");
    sessionStorage.removeItem("checkout_shipping_address_id");
    sessionStorage.removeItem("checkout_billing_address_id");
  };

  // ─── Cart from CartPage via navigate state ──────────────────────────────────
  const cartState = location.state;
  const rawItems = cartState?.selectedItems || cartState?.cartItems || [];
  const cartItems = rawItems.map(item => {
    const mapped = {
      id: item.id,
      product_id: item.id,
      name: item.name,
      price: item.price,
      quantity: item.quantity
    };
    return mapped;
  });
  const subtotal = cartItems.reduce((s, i) => s + i.price * i.quantity, 0);
  const tax = parseFloat((subtotal * 0.18).toFixed(2));
  const total = parseFloat((subtotal).toFixed(2));
  const cartSummary = { items: cartItems, subtotal, shipping: 0, total };

  const steps = [
    { number: 1, name: "Shipping", icon: <FaTruck /> },
    { number: 2, name: "Billing", icon: <FaFileInvoice /> },
    { number: 3, name: "Payment", icon: <FaCreditCard /> },
    { number: 4, name: "Review", icon: <FaCheckCircle /> },
  ];

  // ─── Fetch addresses & wallet on mount ──────────────────────────────────────
  useEffect(() => {
    apiFetch("/address").then(data => {
      if (Array.isArray(data) && data.length > 0) {
        setSavedAddresses(data);
        const def = data.find(a => a.is_default) || data[0];
        if (!selectedAddress) {
          setSelectedAddress(def.id);
          setShowNewAddressForm(false);
          // Pre-fill form with default address
          setFormData(prev => ({
            ...prev,
            firstName: def.name?.split(" ")[0] || "",
            lastName: def.name?.split(" ").slice(1).join(" ") || "",
            address: def.address || "",
            apartment: def.apartment || "",
            city: def.city || "",
            state: def.state || "",
            pincode: def.pincode || "",
            email: def.email || "",
            phone: def.phone || "",
            postOffice: def.post_office || ""
          }));
        }
      } else {
        // No saved addresses — show form directly
        setSavedAddresses([]);
        setSelectedAddress(null);
        setShowNewAddressForm(true);
      }
    }).catch(() => {
      setShowNewAddressForm(true);
    });

    const fetchWallet = async () => {
      try {
        const token = localStorage.getItem("idToken");
        if (!token) return;

        const res = await fetch(
          "https://host.aizoplug.com:3000/app/api/display/wallet/",
          {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
        );

        const data = await res.json();

        console.log("Wallet API response:", data); // 🔥 debug

        if (data.wallets && data.wallets.length > 0) {
          setWalletBalance(parseFloat(data.wallets[0].balance));
        }
      } catch (err) {
        console.error("Wallet fetch error:", err);
      }
    };

    fetchWallet();
  }, []);

  // ─── Pincode lookup — shipping ───────────────────────────────────────────────
  useEffect(() => {
    if (formData.pincode.length !== 6) {
      setPostOffices([]);
      setPinError("");
      return;
    }
    const debounceTimer = setTimeout(async () => {
      try {
        setLoadingPin(true);
        setPinError("");
        const res = await fetch(`https://api.postalpincode.in/pincode/${formData.pincode}`);
        const data = await res.json();
        if (data[0].Status === "Success" && data[0].PostOffice?.length > 0) {
          const offices = data[0].PostOffice;
          setPostOffices(offices);
          const firstOffice = offices[0];
          setFormData(prev => ({
            ...prev,
            city: firstOffice.District,
            state: firstOffice.State,
            postOffice: firstOffice.Name
          }));
        } else {
          setPostOffices([]);
          setPinError("Invalid PIN Code");
        }
      } catch {
        setPinError("Failed to fetch location");
        setPostOffices([]);
      } finally {
        setLoadingPin(false);
      }
    }, 600);
    return () => clearTimeout(debounceTimer);
  }, [formData.pincode]);

  // ─── Pincode lookup — billing ────────────────────────────────────────────────
  useEffect(() => {
    if (!formData.billingPincode || formData.billingPincode.length !== 6) {
      setBillingPostOffices([]);
      setBillingPinError("");
      setFormData(prev => ({ ...prev, billingCity: "", billingState: "", billingPostOffice: "" }));
      return;
    }
    const debounceTimer = setTimeout(async () => {
      try {
        setBillingLoadingPin(true);
        setBillingPinError("");
        const res = await fetch(`https://api.postalpincode.in/pincode/${formData.billingPincode}`);
        const data = await res.json();
        if (data[0].Status === "Success" && data[0].PostOffice?.length > 0) {
          const offices = data[0].PostOffice;
          setBillingPostOffices(offices);
          const firstOffice = offices[0];
          setFormData(prev => ({
            ...prev,
            billingCity: firstOffice.District,
            billingState: firstOffice.State,
            billingPostOffice: firstOffice.Name
          }));
        } else {
          setBillingPostOffices([]);
          setBillingPinError("Invalid PIN Code");
        }
      } catch {
        setBillingPinError("Failed to fetch location");
        setBillingPostOffices([]);
      } finally {
        setBillingLoadingPin(false);
      }
    }, 600);
    return () => clearTimeout(debounceTimer);
  }, [formData.billingPincode]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value
    }));
  };

  const handleAddressSelect = (addressId) => {
    setSelectedAddress(addressId);
    const address = savedAddresses.find(a => a.id === addressId);
    if (address) {
      setFormData(prev => ({
        ...prev,
        firstName: address.name?.split(" ")[0] || "",
        lastName: address.name?.split(" ").slice(1).join(" ") || "",
        address: address.address,
        apartment: address.apartment || "",
        city: address.city,
        state: address.state,
        pincode: address.pincode,
        phone: address.phone,
        postOffice: address.post_office || ""
      }));
    }
  };

  // ─── Save address via API, returns the new address id ───────────────────────
  const saveAddressToApi = async (isShipping) => {
    const name = isShipping
      ? `${formData.firstName} ${formData.lastName}`.trim()
      : `${formData.billingFirstName} ${formData.billingLastName}`.trim();

    const body = isShipping
      ? {
        name,
        phone: formData.phone,
        address: formData.address,
        apartment: formData.apartment,
        email: formData.email,
        city: formData.city,
        state: formData.state,
        pincode: formData.pincode,
        post_office: formData.postOffice
      }
      : {
        name,
        phone: formData.phone,
        address: formData.billingAddress,
        apartment: formData.billingApartment,
        email: formData.email,
        city: formData.billingCity,
        state: formData.billingState,
        pincode: formData.billingPincode,
        post_office: formData.billingPostOffice
      };

    const data = await apiFetch("/address", {
      method: "POST",
      body: JSON.stringify(body)
    });
    return data.id;
  };

  // ─── Step 1 → 2: resolve shipping address id ────────────────────────────────
  const handleContinueToBilling = async () => {
    try {
      let addrId = selectedAddress;

      if (showNewAddressForm) {
        if (!formData.firstName || !formData.phone || !formData.address || !formData.pincode) {
          alert("Please fill all required shipping fields.");
          return;
        }
        // Save address if user checked "save for future" OR always save to get an id
        addrId = await saveAddressToApi(true);
        setShippingAddressId(addrId);

        if (saveInfo) {
          const refreshed = await apiFetch("/address");
          if (Array.isArray(refreshed)) setSavedAddresses(refreshed);
        }
      } else {
        setShippingAddressId(addrId);
      }

      setCurrentStep(2);
    } catch {
      alert("Failed to save address. Please try again.");
    }
  };

  // ─── Step 2 → 3: resolve billing address id ─────────────────────────────────
  const handleContinueToPayment = async () => {
    try {
      if (formData.sameAsShipping) {
        setBillingAddressId(shippingAddressId);
      } else {
        if (!formData.billingFirstName || !formData.billingAddress || !formData.billingPincode) {
          alert("Please fill all required billing fields.");
          return;
        }
        const addrId = await saveAddressToApi(false);
        setBillingAddressId(addrId);
      }
      setCurrentStep(3);
    } catch {
      alert("Failed to save billing address. Please try again.");
    }
  };

  // ─── Wallet payment ──────────────────────────────────────────────────────────
  const handleWalletPayment = async () => {
    if (walletBalance < cartSummary.total) {
      alert("Insufficient wallet balance");
      return;
    }
    setLoading(true);
    try {
      const items = cartSummary.items.map(i => ({ product_id: i.product_id, quantity: i.quantity }));
      const data = await apiFetch("/order/create-wallet", {
        method: "POST",
        body: JSON.stringify({
          billing_address_id: billingAddressId,
          shipping_address_id: shippingAddressId,
          items
        })
      });
      if (data.order_id) {
        setPlacedOrderId(data.order_id);
        setWalletBalance(prev => prev - cartSummary.total);
        setPaymentStatus("success");
        setPaymentMessage("Payment Successful 🎉");
        setOrderDetails({
          orderId: data.order_id,
          total: cartSummary.total,
          paymentMethod: "Wallet",
          date: new Date().toLocaleString()
        });
        clearCheckoutData(); // Clear saved data on success
        setCurrentStep(4);
      } else {
        setPaymentStatus("failed");
        setPaymentMessage("Payment failed");
        setCurrentStep(4);
        alert(data.message || "Wallet payment failed.");
      }
    } catch {
      setPaymentStatus("failed");
      setPaymentMessage("Payment failed");
      setCurrentStep(4);
      alert("Wallet payment failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // ─── Razorpay payment ────────────────────────────────────────────────────────
  const handleRazorpayPayment = async () => {
    setLoading(true);

    try {
      const items = cartSummary.items.map(i => ({
        product_id: i.product_id,
        quantity: i.quantity
      }));

      const rzpData = await apiFetch("/order/create-razorpay", {
        method: "POST",
        body: JSON.stringify({
          billing_address_id: billingAddressId,
          shipping_address_id: shippingAddressId,
          items
        })
      });

      console.log("Razorpay API Response:", rzpData);

      //Extract from rzpOrder
      const order = rzpData.rzpOrder;

      if (!order || !order.id) {
        alert("Failed to create payment order.");
        setLoading(false);
        return;
      }

      const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY_ID, 
        amount: order.amount,
        currency: order.currency,
        name: "AizoPlug",
        image: "https://aizoplug.com/logo.png",
        description: "Order Payment",
        order_id: order.id,

        handler: async (response) => {
          const verify = await apiFetch("/order/verify-razorpay", {
            method: "POST",
            body: JSON.stringify(response)
          });

          if (verify.success) {
            setPaymentStatus("success");
            setPaymentMessage("Payment Successful 🎉");
            setOrderDetails({
              orderId: verify.order_id,
              total: cartSummary.total,
              paymentMethod: "Razorpay",
              date: new Date().toLocaleString()
            });
            clearCheckoutData(); // Clear saved data on success
          } else {
            setPaymentStatus("failed");
            setPaymentMessage("Payment failed");
          }

          setCurrentStep(4);
          setLoading(false);
        },

        prefill: {
          name: `${formData.firstName} ${formData.lastName}`,
          email: formData.email,
          contact: formData.phone
        },

        modal: {
          ondismiss: () => setLoading(false)
        },

        theme: {
          color: "#28d40a"
        }
      };

      const rzp = new window.Razorpay(options);
      rzp.open();

    } catch (err) {
      console.error("❌ Payment Error:", err);
      console.log("Razorpay Response Error:", err?.error || err);
      alert("Payment failed. Try again.");
      setLoading(false);
    }
  };
  // ─── COD payment ─────────────────────────────────────────────────────────────
  const handleCodOrder = async () => {
    setLoading(true);
    try {
      const items = cartSummary.items.map(i => ({ product_id: i.product_id, quantity: i.quantity }));
      const data = await apiFetch("/order/create-cod", {
        method: "POST",
        body: JSON.stringify({
          billing_address_id: billingAddressId,
          shipping_address_id: shippingAddressId,
          items
        })
      });
      if (data.order_id) {
        setPlacedOrderId(data.order_id);
        setPaymentStatus("success");
        setPaymentMessage("Order Placed Successfully!");
        setOrderDetails({
          orderId: data.order_id,
          total: cartSummary.total,
          paymentMethod: "Cash on Delivery",
          date: new Date().toLocaleString()
        });
        clearCheckoutData(); // Clear saved data on success
        setCurrentStep(4);
      } else {
        setPaymentStatus("failed");
        setPaymentMessage("Failed to place order");
        setCurrentStep(4);
        alert(data.message || "Failed to place order.");
      }
    } catch {
      alert("Failed to place order. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Copy order ID to clipboard
  const copyOrderId = () => {
    navigator.clipboard.writeText(placedOrderId || orderDetails?.orderId);
    alert("Order ID copied to clipboard!");
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Final place order on step 4 submit — already handled, just log
    console.log("Order placed:", placedOrderId);
  };

  // Success Component
  const SuccessScreen = () => (
    <div className="success-screen">
      <div className="success-animation">
        <div className="success-checkmark">
          <div className="check-icon">
            <span className="icon-line line-tip"></span>
            <span className="icon-line line-long"></span>
            <div className="icon-circle"></div>
            {/* <div className="icon-fix"></div> */}
          </div>
        </div>
      </div>

      <h2 className="success-title">Order Confirmed!</h2>
      <p className="success-message">{paymentMessage}</p>

      <div className="order-details-card">
        <div className="order-detail-row">
          <span className="detail-label">Order ID:</span>
          <span className="detail-value">#{orderDetails?.orderId || placedOrderId}</span>
          <button onClick={copyOrderId} className="copy-btn">
            <FaCopy /> Copy
          </button>
        </div>
        <div className="order-detail-row">
          <span className="detail-label">Total Amount:</span>
          <span className="detail-value">₹{cartSummary.total.toLocaleString()}</span>
        </div>
        <div className="order-detail-row">
          <span className="detail-label">Payment Method:</span>
          <span className="detail-value">{orderDetails?.paymentMethod || "Online"}</span>
        </div>
        <div className="order-detail-row">
          <span className="detail-label">Order Date:</span>
          <span className="detail-value">{orderDetails?.date || new Date().toLocaleString()}</span>
        </div>
      </div>

      <div className="success-actions">
        <button className="action-btn-checkout primary" onClick={() => window.location.href = "/orders"}>
          View Orders
        </button>
        <button className="action-btn-checkout secondary" onClick={() => window.location.href = "/"}>
          <FaHome /> Continue Shopping
        </button>
        {/* <button className="action-btn outline" onClick={copyOrderId}>
          <FaShare /> Share Receipt
        </button> */}
      </div>

      {/* <div className="delivery-info">
        <FaTruck className="delivery-icon" />
        <div>
          <strong>Estimated Delivery:</strong> 3-5 business days
        </div>
      </div> */}
    </div>
  );

  // Failure Component
  const FailureScreen = () => (
    <div className="failure-screen">
      <div className="failure-animation">
        <div className="failure-icon">✗</div>
      </div>

      <h2 className="failure-title">Payment Failed!</h2>
      <p className="failure-message">{paymentMessage || "Something went wrong with your payment. Please try again."}</p>

      <div className="failure-reasons">
        <h4>Possible reasons:</h4>
        <ul>
          <li>Insufficient balance in account</li>
          <li>Incorrect payment details</li>
          <li>Network connectivity issues</li>
          <li>Bank declined the transaction</li>
        </ul>
      </div>

      <div className="failure-actions">
        <button className="action-btn primary" onClick={() => setCurrentStep(3)}>
          <FaRedoAlt /> Try Again
        </button>
        <button className="action-btn secondary" onClick={() => window.location.href = "/cart"}>
          Edit Cart
        </button>
        <button className="action-btn outline" onClick={() => window.location.href = "/"}>
          Continue Shopping
        </button>
      </div>

      <div className="support-info">
        <p>Need help? Contact our support team at <strong>support@aizoplug.com</strong></p>
      </div>
    </div>
  );

  return (
    <div className="checkout-page-container">
      <Nav />
      <div className="checkout-page">
        <div className="checkout-header">
          <h1 className="checkout-title">Checkout</h1>
        </div>

        {/* Progress Steps */}
        {/* {currentStep !== 4 && (
          <div className="checkout-steps">
            {steps.map(step => (
              <div key={step.number} className={`step ${currentStep >= step.number ? "active" : ""}`}>
                <div className="step-icon">{step.icon}</div>
                <div className="step-name">{step.name}</div>
                {step.number < 3 && <div className="step-line"></div>}
              </div>
            ))}
          </div>
        )} */}

        <div className="checkout-layout">
          {/* Main Checkout Form */}
          <div className="checkout-form">
            <form onSubmit={handleSubmit}>

              {/* Step 1: Shipping Information */}
              {currentStep === 1 && (
                <div className="form-step fade-in">
                  <h2 className="step-title">Shipping Address</h2>

                  {/* Saved Addresses */}
                  {savedAddresses.length > 0 && (
                    <div className="saved-addresses">
                      <h3>Saved Addresses</h3>
                      <div className="address-list">
                        {savedAddresses.map(address => (
                          <div
                            key={address.id}
                            className={`address-card ${selectedAddress === address.id ? "selected" : ""}`}
                            onClick={() => handleAddressSelect(address.id)}
                          >
                            <div className="address-radio">
                              <div className={`custom-radio ${selectedAddress === address.id ? "selected" : ""}`}>
                                {selectedAddress === address.id && <div className="radio-dot"></div>}
                              </div>
                            </div>
                            <div className="address-content">
                              <div className="address-type">
                                {address.is_default && <span className="default-badge">Default</span>}
                              </div>
                              <p className="address-name">{address.name}</p>
                              <p className="address-detail">{address.address}</p>
                              {address.apartment && <p className="address-detail">{address.apartment}</p>}
                              <p className="address-detail">{address.city}, {address.state} - {address.pincode}</p>
                              <p className="address-detail">Phone: {address.phone}</p>
                            </div>
                          </div>
                        ))}
                        <button
                          type="button"
                          className="add-address-btn"
                          onClick={() => setShowNewAddressForm(!showNewAddressForm)}
                        >
                          <span className="btn-icon">+</span>
                          Add New Address
                        </button>
                      </div>
                    </div>
                  )}

                  {/* New Address Form — shown directly if no saved addresses */}
                  {showNewAddressForm && (
                    <div className="new-address-form fade-in">
                      <h3>Enter New Address</h3>
                      <div className="form-row">
                        <div className="form-group">
                          <label>First Name *</label>
                          <input type="text" name="firstName" value={formData.firstName} onChange={handleInputChange} required placeholder="John" />
                        </div>
                        <div className="form-group">
                          <label>Last Name *</label>
                          <input type="text" name="lastName" value={formData.lastName} onChange={handleInputChange} required placeholder="Doe" />
                        </div>
                      </div>

                      <div className="form-group">
                        <label>Address *</label>
                        <input type="text" name="address" value={formData.address} onChange={handleInputChange} required placeholder="Street address" />
                      </div>

                      <div className="form-group">
                        <label>Apartment, suite, etc. (optional)</label>
                        <input type="text" name="apartment" value={formData.apartment} onChange={handleInputChange} placeholder="Apt, suite, unit, etc." />
                      </div>

                      <div className="form-row">
                        <div className="form-group">
                          <label>Phone *</label>
                          <input type="tel" name="phone" value={formData.phone} onChange={handleInputChange} required placeholder="9876543210" pattern="[0-9]{10}" maxLength="10" />
                        </div>
                        <div className="form-group">
                          <label>PIN Code *</label>
                          <input type="text" name="pincode" value={formData.pincode} onChange={handleInputChange} required placeholder="400001" pattern="[0-9]{6}" maxLength="6" />
                          {loadingPin && <small style={{ color: "#555" }}>Fetching location...</small>}
                          {pinError && <small style={{ color: "red" }}>{pinError}</small>}
                        </div>
                        {postOffices.length > 0 && (
                          <div className="form-group">
                            <label>Post Office *</label>
                            <select value={formData.postOffice} onChange={(e) => {
                              const selected = postOffices.find(po => po.Name === e.target.value);
                              setFormData(prev => ({ ...prev, postOffice: selected.Name, city: selected.District, state: selected.State }));
                            }} required>
                              {postOffices.map((po, index) => <option key={index} value={po.Name}>{po.Name}</option>)}
                            </select>
                          </div>
                        )}
                      </div>

                      <div className="form-row">
                        <div className="form-group">
                          <label>City *</label>
                          <input type="text" name="city" value={formData.city} readOnly />
                        </div>
                        <div className="form-group">
                          <label>State *</label>
                          <input type="text" name="state" value={formData.state} readOnly />
                        </div>
                      </div>

                      <div className="form-group">
                        <label>Email *</label>
                        <input type="email" name="email" value={formData.email} onChange={handleInputChange} required placeholder="john@example.com" />
                      </div>

                      <div className="form-checkbox">
                        <label className="custom-checkbox">
                          <input type="checkbox" name="saveInfo" checked={saveInfo} onChange={(e) => setSaveInfo(e.target.checked)} />
                          <span className="checkmark"></span>
                          <span>Save this address for future</span>
                        </label>
                      </div>
                    </div>
                  )}

                  <div className="form-actions">
                    <button type="button" className="btn-next" onClick={handleContinueToBilling} disabled={loading}>
                      Continue to Billing
                    </button>
                  </div>
                </div>
              )}

              {/* Step 2: Billing Information */}
              {currentStep === 2 && (
                <div className="form-step fade-in">
                  <h2 className="step-title">Billing Information</h2>

                  <div className="form-checkbox billing-checkbox">
                    <label className="custom-checkbox">
                      <input
                        type="checkbox"
                        name="sameAsShipping"
                        checked={formData.sameAsShipping}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          if (checked) {
                            setFormData(prev => ({
                              ...prev,
                              sameAsShipping: true,
                              billingFirstName: prev.firstName,
                              billingLastName: prev.lastName,
                              billingAddress: prev.address,
                              billingApartment: prev.apartment,
                              billingCity: prev.city,
                              billingState: prev.state,
                              billingPincode: prev.pincode,
                              billingPostOffice: prev.postOffice,
                              billingCountry: prev.country
                            }));
                          } else {
                            setFormData(prev => ({ ...prev, sameAsShipping: false }));
                          }
                        }}
                      />
                      <span className="checkmark"></span>
                      <span>Same as shipping address</span>
                    </label>
                  </div>

                  <div className="billing-form">
                    <div className="form-row">
                      <div className="form-group">
                        <label>First Name *</label>
                        <input type="text" name="billingFirstName" value={formData.billingFirstName} onChange={handleInputChange} required />
                      </div>
                      <div className="form-group">
                        <label>Last Name *</label>
                        <input type="text" name="billingLastName" value={formData.billingLastName} onChange={handleInputChange} required />
                      </div>
                    </div>

                    <div className="form-group">
                      <label>Address *</label>
                      <input type="text" name="billingAddress" value={formData.billingAddress} onChange={handleInputChange} required />
                    </div>

                    <div className="form-group">
                      <label>Apartment (optional)</label>
                      <input type="text" name="billingApartment" value={formData.billingApartment} onChange={handleInputChange} />
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <label>PIN Code *</label>
                        <input type="text" name="billingPincode" value={formData.billingPincode} onChange={handleInputChange} required pattern="[0-9]{6}" maxLength="6" />
                        {billingLoadingPin && <div className="pin-loader"></div>}
                        {billingPinError && <small style={{ color: "red" }}>{billingPinError}</small>}
                      </div>
                      {billingPostOffices.length > 0 && (
                        <div className="form-group">
                          <label>Post Office *</label>
                          <select value={formData.billingPostOffice} onChange={(e) => {
                            const selected = billingPostOffices.find(po => po.Name === e.target.value);
                            setFormData(prev => ({ ...prev, billingPostOffice: selected.Name, billingCity: selected.District, billingState: selected.State }));
                          }} required>
                            {billingPostOffices.map((po, index) => <option key={index} value={po.Name}>{po.Name}</option>)}
                          </select>
                        </div>
                      )}
                      <div className="form-group">
                        <label>Country</label>
                        <input type="text" name="billingCountry" value={formData.billingCountry} disabled />
                      </div>
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <label>City *</label>
                        <input type="text" name="billingCity" value={formData.billingCity} readOnly />
                      </div>
                      <div className="form-group">
                        <label>State *</label>
                        <input type="text" name="billingState" value={formData.billingState} readOnly />
                      </div>
                    </div>
                  </div>

                  <div className="form-actions">
                    <button type="button" className="btn-back" onClick={() => setCurrentStep(1)}>
                      Back to Shipping
                    </button>
                    <button type="button" className="btn-next" onClick={handleContinueToPayment} disabled={loading}>
                      Continue to Payment
                    </button>
                  </div>
                </div>
              )}

              {/* Step 3: Payment Method */}
              {currentStep === 3 && (
                <div className="form-step fade-in">
                  <h2 className="step-title">Payment Method</h2>

                  <div className="payment-methods">
                    {/* Razorpay */}
                    <label className="payment-option">
                      <input type="radio" name="paymentMethod" value="razorpay" checked={paymentMethod === "razorpay"} onChange={(e) => setPaymentMethod(e.target.value)} />
                      <span>Pay Online (Razorpay)</span>
                    </label>

                    {/* Wallet */}
                    <label className="payment-option">
                      <input
                        type="radio"
                        name="paymentMethod"
                        value="wallet"
                        checked={paymentMethod === "wallet"}
                        onChange={(e) => setPaymentMethod(e.target.value)}
                      />
                      <span>Wallet Balance (₹{walletBalance.toLocaleString("en-IN")})</span>
                    </label>

                    {/* COD */}
                    <label className="payment-option">
                      <input type="radio" name="paymentMethod" value="cod" checked={paymentMethod === "cod"} onChange={(e) => setPaymentMethod(e.target.value)} />
                      <span>Cash on Delivery</span>
                    </label>
                  </div>

                  <div className="form-actions">
                    <button type="button" className="btn-back" onClick={() => setCurrentStep(2)}>
                      Back to Billing
                    </button>
                    <button
                      type="button"
                      className="btn-next"
                      disabled={loading}
                      onClick={() => {
                        if (paymentMethod === "razorpay") handleRazorpayPayment();
                        else if (paymentMethod === "wallet") handleWalletPayment();
                        else if (paymentMethod === "cod") handleCodOrder();
                      }}
                    >
                      {loading ? "Processing…" : "Checkout"}
                    </button>
                  </div>
                </div>
              )}

              {/* Step 4: Review Order */}
              {currentStep === 4 && (
                <div className="form-step fade-in">
                  {paymentStatus === "success" && <SuccessScreen />}
                  {paymentStatus === "failed" && <FailureScreen />}
                  {paymentStatus === null && (
                   setCurrentStep(1)
                  )}
                </div>
              )}
            </form>
          </div>

          {/* Order Summary Sidebar */}
          {currentStep !== 4 && (
            <div className="checkout-summary">
              <h2>Order Summary</h2>
              <div className="summary-items">
                {cartSummary.items.map(item => (
                  <div key={item.id} className="summary-item">
                    <div className="item-details">
                      <span className="item-quantity">{item.quantity}x</span>
                      <span className="item-name">{item.name}</span>
                    </div>
                    <span className="item-price">₹{(item.price * item.quantity).toLocaleString()}</span>
                  </div>
                ))}
              </div>
              <div className="summary-divider"></div>
              <div className="summary-row"><span>Subtotal</span><span>₹{cartSummary.subtotal.toLocaleString()}</span></div>
              <div className="summary-row"><span>Shipping</span><span className="free-shipping">FREE</span></div>
              <div className="summary-divider"></div>
              <div className="summary-row total"><span>Total</span><span className="total-amount">₹{cartSummary.total.toLocaleString()}</span></div>
              <div className="secure-checkout">
                <span className="secure-icon">🔒</span>
                <span>Secure Checkout</span>
              </div>
              <div className="guarantee-badge">
                <div className="guarantee-icon">✓</div>
                <div className="guarantee-text">
                  <strong>30-Day Money-Back Guarantee</strong>
                  <span>Easy returns within 30 days</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default CheckoutPage;